import { useMemo, useState } from "react";
import { genres, titles } from "@/mocks/movieData";
import SectionHeading, { Section } from "./SectionHeading";

const allFilter = { id: "all", name: "全部" };

export default function TitleCatalog() {
  const [activeGenre, setActiveGenre] = useState(allFilter.id);
  const [keyword, setKeyword] = useState("");

  const filterOptions = useMemo(
    () => [allFilter, ...genres.map((g) => ({ id: g.id, name: g.name }))],
    []
  );

  const filtered = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    return titles.filter((item) => {
      const matchGenre = activeGenre === allFilter.id || item.genreId === activeGenre;
      const matchKeyword =
        kw.length === 0 ||
        item.name.toLowerCase().includes(kw) ||
        item.tags.some((tag) => tag.toLowerCase().includes(kw)) ||
        item.genre.toLowerCase().includes(kw);
      return matchGenre && matchKeyword;
    });
  }, [activeGenre, keyword]);

  return (
    <Section id="titles" className="bg-background-50">
      <SectionHeading
        eyebrow="片单速览"
        title="按类型或关键词，快速找到想看的片单"
        description="下面的筛选与搜索只作用于本页已列出的内容，不会联网请求。选择类型标签或输入关键词即可即时筛选，清空条件后列表会恢复完整。"
      />

      <div className="rounded-lg bg-background-100 border border-background-200 p-4 md:p-5 mb-6">
        <div className="flex flex-col lg:flex-row lg:items-center gap-4">
          <div className="relative w-full lg:max-w-xs">
            <i
              className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"
              aria-hidden="true"
            ></i>
            <input
              type="text"
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              placeholder="搜索片名或标签，如：科幻、剧集"
              aria-label="按关键词筛选片单"
              className="w-full pl-9 pr-9 py-2.5 rounded-md bg-background-50 border border-background-300 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
            />
            {keyword ? (
              <button
                type="button"
                onClick={() => setKeyword("")}
                aria-label="清空搜索"
                className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center rounded-full text-foreground-400 hover:text-primary-700 hover:bg-background-200 transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-sm" aria-hidden="true"></i>
              </button>
            ) : null}
          </div>

          <div className="flex flex-wrap gap-2">
            {filterOptions.map((option) => {
              const isActive = option.id === activeGenre;
              return (
                <button
                  key={option.id}
                  type="button"
                  aria-pressed={isActive}
                  onClick={() => setActiveGenre(option.id)}
                  className={`px-3.5 py-1.5 rounded-full text-sm font-label whitespace-nowrap border transition-colors cursor-pointer ${
                    isActive
                      ? "bg-primary-500 text-background-50 border-primary-500"
                      : "bg-background-50 text-foreground-700 border-background-300 hover:border-primary-300 hover:text-primary-700"
                  }`}
                >
                  {option.name}
                </button>
              );
            })}
          </div>
        </div>
        <p className="mt-3 text-xs md:text-sm text-foreground-500 font-label">
          共 {filtered.length} 条结果
          {activeGenre !== allFilter.id ? ` · 类型：${genres.find((g) => g.id === activeGenre)?.name ?? ""}` : ""}
          {keyword.trim() ? ` · 关键词：${keyword.trim()}` : ""}
        </p>
      </div>

      {filtered.length > 0 ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
          {filtered.map((item) => (
            <article
              key={item.id}
              className="group rounded-lg bg-background-50 border border-background-200 overflow-hidden"
            >
              <div className="relative w-full h-52 md:h-64 overflow-hidden bg-background-200">
                <img
                  src={item.poster}
                  alt={`${item.name}（${item.year}）影视海报`}
                  title={`${item.name} 影视海报`}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
                <span className="absolute top-2 left-2 px-2 py-0.5 rounded-full bg-black/55 text-background-50 text-[11px] font-label backdrop-blur">
                  {item.genre}
                </span>
              </div>
              <div className="p-3 md:p-4">
                <h3 className="font-heading text-base font-bold text-foreground-950 truncate">{item.name}</h3>
                <p className="mt-0.5 text-xs text-foreground-500 font-label">{item.year}</p>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {item.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-900 text-[11px] font-label"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
                <p className="mt-2.5 text-xs md:text-sm text-foreground-600 leading-relaxed line-clamp-3">
                  {item.note}
                </p>
              </div>
            </article>
          ))}
        </div>
      ) : (
        <div className="rounded-lg border border-dashed border-background-300 bg-background-100 py-14 px-4 text-center">
          <i className="ri-movie-line text-3xl text-foreground-400" aria-hidden="true"></i>
          <p className="mt-3 text-sm text-foreground-700">没有找到匹配的片单</p>
          <p className="mt-1 text-xs text-foreground-500">试试更换关键词，或点击「全部」查看完整列表。</p>
          <button
            type="button"
            onClick={() => {
              setKeyword("");
              setActiveGenre(allFilter.id);
            }}
            className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-primary-500 text-background-50 text-sm font-label whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
          >
            <i className="ri-refresh-line" aria-hidden="true"></i>
            重置筛选条件
          </button>
        </div>
      )}
    </Section>
  );
}